import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/shared/api/client';
import type { Project, ProjectStatus } from '@/shared/api/types';

export interface ProjectFilters {
  q?: string;
  status?: ProjectStatus | '';
  customerId?: string;
}

export function useProjects(filters: ProjectFilters) {
  const params = new URLSearchParams({ limit: '100' });
  if (filters.q) params.set('q', filters.q);
  if (filters.status) params.set('status', filters.status);
  if (filters.customerId) params.set('customerId', filters.customerId);
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => api<Paginated<Project>>(`/api/projects?${params.toString()}`),
  });
}

export function useProject(id: string | null) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => api<Project>(`/api/projects/${id}`),
    enabled: !!id,
  });
}

export interface CreateProjectInput {
  customerId: string;
  objectId: string;
  title: string;
  description?: string;
  scopeOriginal?: string;
  plannedStart?: string;
  plannedEnd?: string;
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api<Project>('/api/projects', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ProjectStatus }) =>
      api<Project>(`/api/projects/${id}/status`, { method: 'PATCH', body: { status } }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export interface CaptureLinkResponse {
  url: string;
  expiresAt: string;
}

export function useCreateCaptureLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api<CaptureLinkResponse>(`/api/projects/${projectId}/capture-link`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (_d, projectId) => qc.invalidateQueries({ queryKey: ['project', projectId] }),
  });
}

export function useRevokeCaptureLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api<{ revoked: boolean }>(`/api/projects/${projectId}/capture-link`, { method: 'DELETE' }),
    onSuccess: (_d, projectId) => qc.invalidateQueries({ queryKey: ['project', projectId] }),
  });
}

// Erlaubte Folge-Status je Ist-Status (Spiegel der Backend-Zustandsmaschine,
// Kap. 6.1). Das Backend bleibt die Wahrheit - das hier steuert nur, welche
// Buttons angeboten werden.
// EXAKTE Kopie der Backend-Kanten (project-status.ts). CLOSED gibt es nur
// aus CUSTOMER_CONFIRMED/REJECTED - ein Auftrag wird nicht einfach zugemacht,
// er durchlaeuft den Nachweisprozess.
export const NEXT_STATUS: Record<ProjectStatus, ProjectStatus[]> = {
  DRAFT: ['SUBMITTED'],
  SUBMITTED: ['REVIEW_REQUIRED'],
  REVIEW_REQUIRED: ['READY_TO_SEND', 'DRAFT'],
  READY_TO_SEND: ['AWAITING_CUSTOMER', 'REVIEW_REQUIRED'],
  AWAITING_CUSTOMER: ['CUSTOMER_CONFIRMED', 'CUSTOMER_REJECTED', 'READY_TO_SEND', 'REVIEW_REQUIRED'],
  CUSTOMER_CONFIRMED: ['CLOSED'],
  CUSTOMER_REJECTED: ['REVIEW_REQUIRED', 'CLOSED'],
  CLOSED: [],
};

export const STATUS_ACTION_LABEL: Record<ProjectStatus, string> = {
  DRAFT: 'Zurück an Außendienst',
  SUBMITTED: 'Meldung eingegangen',
  REVIEW_REQUIRED: 'Zur Prüfung',
  READY_TO_SEND: 'Bereit zum Versand',
  AWAITING_CUSTOMER: 'An Kunde gesendet',
  CUSTOMER_CONFIRMED: 'Kunde hat bestätigt',
  CUSTOMER_REJECTED: 'Kunde hat abgelehnt',
  CLOSED: 'Abschließen',
};
